import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEffect, lazy, Suspense } from 'react';
import { useAuth } from '@clerk/react';
import {
  AppShell,
  Burger,
  Group,
  Text,
  NavLink,
  UnstyledButton,
  Stack,
  Box,
  Divider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconHome,
  IconUsers,
  IconBuildingStore,
  IconSettings,
  IconShoppingBag,
  IconBoxSeam,
  IconDashboard,
  IconUserPlus,
  IconShoppingCart,
  IconCrown,
  IconMessage,
  IconSparkles,
  IconBook,
  IconCategory,
  IconChevronRight,
  IconFileText,
  IconWallet,
  IconRefresh,
  IconLock,
  IconUserCog,
  IconBell,
  IconChartBar,
  IconHeart,
  IconBan,
  IconFlag,
  IconAlbum,
  IconReceipt,
  IconCashBanknote,
  IconHeartStar,
  IconGift,
  IconShieldCheck,
} from '@tabler/icons-react';

import App from './App';
import { AdminRoute, AdminDashboard } from './components/admin';
import AuthButton from './components/auth/AuthButton';

// LOCAL DEV ONLY quick-login. The `import.meta.env.DEV` literal is statically
// false in production builds, so Rollup drops the dynamic import and neither
// DevSignIn nor src/dev/personas.js (seed credentials) reach the bundle.
const DevSignIn = import.meta.env.DEV
  ? lazy(() => import('./components/auth/DevSignIn'))
  : () => null;
import CartButton from './components/cart/CartButton';
import NotificationBell from './components/notifications/NotificationBell';
import { useSession } from './api';
import { loadCart } from './store/slices/cartSlice';
import BlogPage from './pages/BlogPage';
import BecomePrincessPage from './pages/BecomePrincessPage';
import PiggySubscriptionPage from './pages/PiggySubscriptionPage';
import IdentityVerificationPage from './pages/IdentityVerificationPage';
import SellerDashboardPage from './pages/seller/SellerDashboardPage';
import SellerAnalyticsPage from './pages/seller/SellerAnalyticsPage';
import BuildProfilePage from './pages/BuildProfilePage';
import BuildPayPiggyProfilePage from './pages/BuildPayPiggyProfilePage';
import CreateProductPage from './pages/seller/CreateProductPage';
import UsernamePrompt from './components/auth/UsernamePrompt';
import BrowseCategoriesPage from './pages/BrowseCategoriesPage';
import UserManagement from './components/admin/UserManagement';
import ReportsQueue from './components/admin/ReportsQueue';
import AdminPayoutsPage from './pages/admin/AdminPayoutsPage';

// ── Seller nav items ─────────────────────────────────────────────────────────
const SELLER_NAV = [
  { label: 'My Content',    icon: IconFileText,    path: '/seller/products' },
  { label: 'Albums',        icon: IconAlbum,       path: '/seller/albums' },
  { label: 'Orders',        icon: IconShoppingBag, path: '/seller/orders' },
  { label: 'Buyers',        icon: IconUsers,       path: '/seller/buyers' },
  { label: 'Earnings',      icon: IconWallet,      path: '/seller/earnings' },
  { label: 'Messages',      icon: IconMessage,     path: '/messages' },
  { label: 'Subscriptions', icon: IconRefresh,     path: '/subscription' },
  { label: 'Vault',         icon: IconLock,        path: '/seller/vault' },
  { label: 'Manager',       icon: IconUserCog,     path: '/seller/dashboard' },
  { label: 'Notifications', icon: IconBell,        path: '/notifications' },
  { label: 'Sellers',       icon: IconBuildingStore, path: '/princesses' },
  { label: 'Categories',    icon: IconCategory,    path: '/browse-categories' },
  { label: 'Analytics',     icon: IconChartBar,    path: '/seller/analytics' },
];

const SellerNavItem = ({ item, active, onClick }) => (
  <UnstyledButton
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '14px 20px',
      background: active ? '#7c3aed' : 'transparent',
      color: active ? '#fff' : '#212529',
      borderRadius: 0,
      transition: 'background 0.12s',
    }}
  >
    <Group gap="md" align="center">
      <item.icon size={22} color={active ? '#fff' : '#495057'} />
      <Text size="md" fw={active ? 600 : 400} style={{ color: active ? '#fff' : '#212529' }}>
        {item.label}
      </Text>
    </Group>
    <IconChevronRight size={16} color={active ? '#fff' : '#adb5bd'} />
  </UnstyledButton>
);

const SellerNavbar = ({ close }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Stack gap={0} style={{ height: '100%' }}>
      {/* Purple header */}
      <Box style={{
        background: 'linear-gradient(135deg, #7c3aed 0%, #9f38cc 100%)',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Text fw={900} size="xl" style={{ color: '#fff', letterSpacing: '-0.5px' }}>
          PediPiggy
        </Text>
        <Text fw={700} size="sm" style={{ color: '#fff', letterSpacing: 1.5 }}>
          PRINCESS
        </Text>
      </Box>

      {/* Nav items */}
      <Box style={{ flex: 1, overflowY: 'auto' }}>
        {SELLER_NAV.map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path === '/seller/dashboard' && location.pathname.startsWith('/seller/dashboard'));
          return (
            <Box key={item.label}>
              <SellerNavItem
                item={item}
                active={isActive}
                onClick={() => { navigate(item.path); close(); }}
              />
              <Divider style={{ margin: 0 }} />
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const Layout = ({ children }) => {
  const [opened, { toggle, close }] = useDisclosure();
  const { isSignedIn } = useAuth();
  const { isAdmin, isSeller } = useSession();
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    dispatch(loadCart());
  }, [dispatch]);

  // Close navbar when route changes
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 250,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group h="100%" gap="xs" wrap="nowrap">
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
            />
            <Text size={{ base: 'md', sm: 'xl' }} fw={700} c="pink">
              PediPiggy
            </Text>
          </Group>
          <Group gap={{ base: 4, sm: 'sm' }} wrap="nowrap">
            {isSignedIn && <NotificationBell />}
            <CartButton />
            <AuthButton />
            {import.meta.env.DEV && (
              <Suspense fallback={null}>
                <DevSignIn />
              </Suspense>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p={isSeller ? 0 : 'md'}>
        {isSeller ? (
          <SellerNavbar close={close} />
        ) : (
          <>
            <NavLink
              component={Link}
              to="/"
              label="Home"
              leftSection={<IconHome size="1rem" />}
            />

            <NavLink
              component={Link}
              to="/blog"
              label="Blog"
              leftSection={<IconBook size="1rem" />}
            />

            <NavLink
              component={Link}
              to="/products"
              label="Browse Products"
              leftSection={<IconShoppingBag size="1rem" />}
            />

            <NavLink
              component={Link}
              to="/princesses"
              label="Princesses"
              leftSection={<IconBuildingStore size="1rem" />}
            />

            <NavLink
              component={Link}
              to="/cart"
              label="Shopping Cart"
              leftSection={<IconShoppingCart size="1rem" />}
            />

            <NavLink
              component={Link}
              to="/browse-categories"
              label="Categories"
              leftSection={<IconCategory size="1rem" />}
            />

            {isSignedIn && !isSeller && (
              <NavLink
                component={Link}
                to="/become-princess"
                label="Become a Princess"
                leftSection={<IconUserPlus size="1rem" />}
              />
            )}

            {isSignedIn && isAdmin && (
              <>
                <NavLink
                  component={Link}
                  to="/admin"
                  label="Admin Dashboard"
                  leftSection={<IconSettings size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/admin/reports"
                  label="Admin reports"
                  leftSection={<IconFlag size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/admin/payouts"
                  label="Admin payouts"
                  leftSection={<IconCashBanknote size="1rem" />}
                />
              </>
            )}

            {isSignedIn && (
              <>
                <NavLink
                  component={Link}
                  to="/messages"
                  label="Messages"
                  leftSection={<IconMessage size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/favorites"
                  label="Favorites"
                  leftSection={<IconHeart size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/notifications"
                  label="Notifications"
                  leftSection={<IconBell size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/purchases"
                  label="My Purchases"
                  leftSection={<IconReceipt size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/my-subscriptions"
                  label="My Subscriptions"
                  leftSection={<IconHeartStar size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/referrals"
                  label="Referrals"
                  leftSection={<IconGift size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/settings/blocked"
                  label="Blocked users"
                  leftSection={<IconBan size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/settings/verification"
                  label="Buyer ID verification"
                  leftSection={<IconShieldCheck size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/my-requests"
                  label="My Special Requests"
                  leftSection={<IconSparkles size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/subscription"
                  label="Subscription"
                  leftSection={<IconCrown size="1rem" />}
                />
                <NavLink
                  component={Link}
                  to="/profile"
                  label="Profile"
                  leftSection={<IconUsers size="1rem" />}
                />
              </>
            )}
          </>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        {children}
      </AppShell.Main>

      <UsernamePrompt />
    </AppShell>
  );
};

const Router = () => {
  return (
    <BrowserRouter>
      <Layout>
        <Routes>
          <Route path="/*" element={<App />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/become-princess" element={<BecomePrincessPage />} />
          <Route path="/piggy-subscription" element={<PiggySubscriptionPage />} />
          <Route path="/verify-identity" element={<IdentityVerificationPage />} />
          <Route path="/seller/dashboard" element={<SellerDashboardPage />} />
          <Route path="/seller/analytics" element={<SellerAnalyticsPage />} />
          <Route path="/build-profile" element={<BuildProfilePage />} />
          <Route path="/build-pay-piggy-profile" element={<BuildPayPiggyProfilePage />} />
          <Route path="/browse-categories" element={<BrowseCategoriesPage />} />
          <Route path="/admin/users" element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          } />
          <Route path="/admin/reports" element={
            <AdminRoute>
              <ReportsQueue />
            </AdminRoute>
          } />
          <Route path="/admin/payouts" element={
            <AdminRoute>
              <AdminPayoutsPage />
            </AdminRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
};

export default Router;
